import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectEdit from './pages/ProjectEdit';
import TimesheetDetail from './pages/TimesheetDetail';
import DailyReportDetail from './pages/DailyReportDetail';
import SafetyReportDetail from './pages/SafetyReportDetail';
import ToolboxTalkDetail from './pages/ToolboxTalkDetail';
import ErrorBoundary from './components/ErrorBoundary';
import Timesheets from './pages/Timesheets';
import DailyReports from './pages/DailyReports';
import SafetyReports from './pages/SafetyReports';
import ToolboxTalks from './pages/ToolboxTalks';
import CostCodes from './pages/CostCodes';
import CostCodeDetail from './pages/CostCodeDetail';
import Profile from './pages/Profile';
import ProjectCreate from './pages/ProjectCreate';
import BudgetDetail from './pages/BudgetDetail';
import TimesheetCreate from './pages/TimesheetCreate';
import DailyReportCreate from './pages/DailyReportCreate';
import SafetyReportCreate from './pages/SafetyReportCreate';
import CostCodeCreate from './pages/CostCodeCreate';
import ToolboxTalkCreate from './pages/ToolboxTalkCreate';
import AnalyticsDashboard from './pages/AnalyticsDashboard';

// Project & Resource Management
import Equipment from './pages/Equipment';
import EquipmentCreate from './pages/EquipmentCreate';
import EquipmentDetail from './pages/EquipmentDetail';
import Materials from './pages/Materials';
import MaterialCreate from './pages/MaterialCreate';
import MaterialDetail from './pages/MaterialDetail';
import Subcontractors from './pages/Subcontractors';
import SubcontractorCreate from './pages/SubcontractorCreate';
import Documents from './pages/Documents';
import DocumentUpload from './pages/DocumentUpload';
import QualityControl from './pages/QualityControl';
import QualityControlCreate from './pages/QualityControlCreate';

// Financial
import Budgets from './pages/Budgets';
import BudgetCreate from './pages/BudgetCreate';
import Invoices from './pages/Invoices';
import InvoiceCreate from './pages/InvoiceCreate';
import InvoiceDetail from './pages/InvoiceDetail';
import FinancialReports from './pages/FinancialReports';
import ChangeOrders from './pages/ChangeOrders';

// Communication
import PhotoDocumentation from './pages/PhotoDocumentation';
import IssueTracking from './pages/IssueTracking';
import ClientPortal from './pages/ClientPortal';

// Administration
import CompanyManagement from './pages/CompanyManagement';
import CompanyCreate from './pages/CompanyCreate';
import RoleManagement from './pages/RoleManagement';
import UserManagement from './pages/UserManagement';

// Business
import Subscriptions from './pages/Subscriptions';
import SubscriptionCreate from './pages/SubscriptionCreate';

// Mobile Management
import PushNotifications from './pages/PushNotifications';
import GPSTracking from './pages/GPSTracking';
import CameraManagement from './pages/CameraManagement';
import MobileSync from './pages/MobileSync';

// Procurement
import ProcurementDashboard from './pages/Procurement/ProcurementDashboard';
import Vendors from './pages/Procurement/Vendors';
import PurchaseOrders from './pages/Procurement/PurchaseOrders';
import RFQs from './pages/Procurement/RFQs';
import ProcurementItems from './pages/Procurement/ProcurementItems';
import VendorCreate from './pages/Procurement/VendorCreate.jsx';
import PurchaseOrderCreate from './pages/Procurement/PurchaseOrderCreate.jsx';
import RFQCreate from './pages/Procurement/RFQCreate.jsx';
import ProcurementItemCreate from './pages/Procurement/ProcurementItemCreate.jsx';

// Designs
import DesignDashboard from './pages/Designs/DesignDashboard';
import Drawings from './pages/Designs/Drawings';
import DrawingCreate from './pages/Designs/DrawingCreate';
import DesignRevisions from './pages/Designs/DesignRevisions';
import DesignApprovals from './pages/Designs/DesignApprovals';

// Project Planning
import {
  ProjectPlanningDashboard,
  GanttChart,
  PrimaveraView,
  AIAnalysis,
  ScenarioComparison,
} from './pages/ProjectPlanning';
import AIDetails from './pages/ProjectPlanning/AIDetails';
import AIVisualOverview from './pages/ProjectPlanning/AIVisualOverview';
import ScheduleCreate from './pages/ProjectPlanning/ScheduleCreate';
import NotFound from './pages/NotFound';
// AI Endpoints pages
import ScheduleOptimizationPage from './pages/AIEndpoints/ScheduleOptimizationPage';
import ScheduleOptimizationList from './pages/AIEndpoints/ScheduleOptimizationList';
import VideoAnalyzerPage from './pages/AIEndpoints/VideoAnalyzerPage';
import VideoAnalyzerList from './pages/AIEndpoints/VideoAnalyzerList';
import DelayAnalysisPage from './pages/AIEndpoints/DelayAnalysisPage';
import DelayAnalysisList from './pages/AIEndpoints/DelayAnalysisList';
import OverallReportPage from './pages/AIEndpoints/OverallReportPage';
import OverallReportList from './pages/AIEndpoints/OverallReportList';
import RiskForecastPage from './pages/AIEndpoints/RiskForecastPage';
import RiskForecastList from './pages/AIEndpoints/RiskForecastList';
import AIResultDetail from './pages/AIEndpoints/AIResultDetail';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2c2c2c',
      light: '#4a4a4a',
      dark: '#1a1a1a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f8f9fa',
      light: '#ffffff',
      dark: '#e9ecef',
      contrastText: '#2c2c2c',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c2c2c',
      secondary: '#6c757d',
    },
    divider: '#dee2e6',
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e9ecef',
      300: '#dee2e6',
      400: '#ced4da',
      500: '#adb5bd',
      600: '#6c757d',
      700: '#495057',
      800: '#343a40',
      900: '#212529',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    h1: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#2c2c2c',
      lineHeight: 1.3,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#2c2c2c',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#2c2c2c',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 500,
      color: '#2c2c2c',
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#2c2c2c',
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#2c2c2c',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      color: '#2c2c2c',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      color: '#6c757d',
      lineHeight: 1.4,
    },
    button: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#6c757d',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#2c2c2c',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderBottom: '1px solid #dee2e6',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #dee2e6',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderRadius: 6,
          padding: '8px 16px',
        },
        contained: {
          backgroundColor: '#2c2c2c',
          color: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          '&:hover': {
            backgroundColor: '#4a4a4a',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          },
        },
        outlined: {
          borderColor: '#dee2e6',
          color: '#2c2c2c',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#2c2c2c',
          },
        },
        text: {
          color: '#2c2c2c',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            '& fieldset': {
              borderColor: '#dee2e6',
            },
            '&:hover fieldset': {
              borderColor: '#2c2c2c',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2c2c2c',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #dee2e6',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8f9fa',
          color: '#2c2c2c',
          border: '1px solid #dee2e6',
          fontSize: '0.75rem',
          height: 24,
        },
        filled: {
          backgroundColor: '#2c2c2c',
          color: '#ffffff',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8f9fa',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8f9fa',
          color: '#2c2c2c',
          fontWeight: 600,
          fontSize: '0.8125rem',
          borderBottom: '1px solid #dee2e6',
        },
        body: {
          fontSize: '0.875rem',
          borderBottom: '1px solid #dee2e6',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f8f9fa',
          borderRight: '1px solid #dee2e6',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: '#2c2c2c',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#4a4a4a',
            },
          },
          '&:hover': {
            backgroundColor: '#e9ecef',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <div>Loading...</div>
        </Box>
      </Box>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" state={{ from: location }} replace />;
};

// Role-based route guard for pages requiring specific roles
const RoleRoute: React.FC<{ roles: string[]; children: React.ReactNode }> = ({ roles, children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <div>Loading...</div>
        </Box>
      </Box>
    );
  }
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }
  return roles.includes(user.role) ? <>{children}</> : <Navigate to="/projects" />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <div>Loading...</div>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar onMenuClick={handleSidebarToggle} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          ml: { xs: 0, sm: '120px' },
        }}
      >
        <Routes>
          {/* If user is authenticated and visits /login, send them to dashboard */}
          <Route path="/login" element={<Navigate to="/dashboard" />} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/create"
            element={
              <RoleRoute roles={["admin", "project_manager"]}>
                <ProjectCreate />
              </RoleRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute>
                <ProjectEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets"
            element={
              <ProtectedRoute>
                <Timesheets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets/create"
            element={
              <ProtectedRoute>
                <TimesheetCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets/create"
            element={
              <ProtectedRoute>
                <TimesheetCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets/:id"
            element={
              <ProtectedRoute>
                <TimesheetDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-reports"
            element={
              <ProtectedRoute>
                <DailyReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-reports/create"
            element={
              <ProtectedRoute>
                <DailyReportCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-reports/:id"
            element={
              <ProtectedRoute>
                <DailyReportDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safety-reports"
            element={
              <ProtectedRoute>
                <SafetyReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safety-reports/create"
            element={
              <ProtectedRoute>
                <SafetyReportCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safety-reports/:id"
            element={
              <ProtectedRoute>
                <SafetyReportDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/toolbox-talks/create"
            element={
              <ProtectedRoute>
                <ToolboxTalkCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/toolbox-talks/:id"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <ToolboxTalkDetail />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/toolbox-talks"
            element={
              <ProtectedRoute>
                <ToolboxTalks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cost-codes/create"
            element={
              <ProtectedRoute>
                <CostCodeCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cost-codes/:id"
            element={
              <ProtectedRoute>
                <CostCodeDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cost-codes"
            element={
              <ProtectedRoute>
                <CostCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          
          {/* Project & Resource Management */}
          <Route
            path="/equipment"
            element={
              <ProtectedRoute>
                <Equipment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/create"
            element={
              <ProtectedRoute>
                <EquipmentCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/:id"
            element={
              <ProtectedRoute>
                <EquipmentDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/materials"
            element={
              <ProtectedRoute>
                <Materials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/materials/create"
            element={
              <ProtectedRoute>
                <MaterialCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/materials/:id"
            element={
              <ProtectedRoute>
                <MaterialDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subcontractors"
            element={
              <ProtectedRoute>
                <Subcontractors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subcontractors/create"
            element={
              <ProtectedRoute>
                <SubcontractorCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/upload"
            element={
              <ProtectedRoute>
                <DocumentUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quality-control"
            element={
              <ProtectedRoute>
                <QualityControl />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quality-control/create"
            element={
              <ProtectedRoute>
                <QualityControlCreate />
              </ProtectedRoute>
            }
          />
          
          {/* Financial */}
          <Route
            path="/budgets"
            element={
              <ProtectedRoute>
                <Budgets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/budgets/:id"
            element={
              <ProtectedRoute>
                <BudgetDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/budgets/create"
            element={
              <ProtectedRoute>
                <BudgetCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/create"
            element={
              <ProtectedRoute>
                <InvoiceCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports"
            element={
              <ProtectedRoute>
                <FinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-orders"
            element={
              <ProtectedRoute>
                <ChangeOrders />
              </ProtectedRoute>
            }
          />
          
          {/* Communication */}
          <Route
            path="/photo-documentation"
            element={
              <ProtectedRoute>
                <PhotoDocumentation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/issue-tracking"
            element={
              <ProtectedRoute>
                <IssueTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-portal"
            element={
              <ProtectedRoute>
                <ClientPortal />
              </ProtectedRoute>
            }
          />
          
          {/* Administration */}
          <Route
            path="/company-management"
            element={
              <ProtectedRoute>
                <CompanyManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-management/create"
            element={
              <ProtectedRoute>
                <CompanyCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/role-management"
            element={
              <ProtectedRoute>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          
          {/* Business */}
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions/create"
            element={
              <ProtectedRoute>
                <SubscriptionCreate />
              </ProtectedRoute>
            }
          />
          
          {/* Mobile Management */}
          <Route
            path="/push-notifications"
            element={
              <ProtectedRoute>
                <PushNotifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gps-tracking"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <GPSTracking />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/camera-management"
            element={
              <ProtectedRoute>
                <CameraManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mobile-sync"
            element={
              <ProtectedRoute>
                <MobileSync />
              </ProtectedRoute>
            }
          />
          
          {/* Procurement */}
          <Route
            path="/procurement"
            element={
              <ProtectedRoute>
                <ProcurementDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors"
            element={
              <ProtectedRoute>
                <Vendors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendors/create"
            element={
              <ProtectedRoute>
                <VendorCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute>
                <PurchaseOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/create"
            element={
              <ProtectedRoute>
                <PurchaseOrderCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rfqs"
            element={
              <ProtectedRoute>
                <RFQs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rfqs/create"
            element={
              <ProtectedRoute>
                <RFQCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement-items"
            element={
              <ProtectedRoute>
                <ProcurementItems />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement-items/create"
            element={
              <ProtectedRoute>
                <ProcurementItemCreate />
              </ProtectedRoute>
            }
          />
          
          {/* Designs */}
          <Route
            path="/designs"
            element={
              <ProtectedRoute>
                <DesignDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drawings"
            element={
              <ProtectedRoute>
                <Drawings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drawings/create"
            element={
              <ProtectedRoute>
                <DrawingCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/design-revisions"
            element={
              <ProtectedRoute>
                <DesignRevisions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/design-approvals"
            element={
              <ProtectedRoute>
                <DesignApprovals />
              </ProtectedRoute>
            }
          />
          
          {/* Project Planning */}
          <Route
            path="/project-planning"
            element={
              <ProtectedRoute>
                <ProjectPlanningDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/gantt"
            element={
              <ProtectedRoute>
                <GanttChart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/gantt/:scheduleId"
            element={
              <ProtectedRoute>
                <GanttChart />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/primavera"
            element={
              <ProtectedRoute>
                <PrimaveraView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/primavera/:projectId"
            element={
              <ProtectedRoute>
                <PrimaveraView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/schedules/new"
            element={
              <ProtectedRoute>
                <ScheduleCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai-analysis"
            element={
              <ProtectedRoute>
                <AIAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai-analysis/:projectId"
            element={
              <ProtectedRoute>
                <AIAnalysis />
              </ProtectedRoute>
            }
          />
        <Route 
          path="/project-planning/ai-details" 
          element={
            <ProtectedRoute>
              <AIDetails />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/project-planning/ai-visuals" 
          element={
            <ProtectedRoute>
              <AIVisualOverview />
            </ProtectedRoute>
          }
        />
          <Route
            path="/project-planning/scenarios"
            element={
              <ProtectedRoute>
                <ScenarioComparison />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/scenarios/:projectId"
            element={
              <ProtectedRoute>
                <ScenarioComparison />
              </ProtectedRoute>
            }
          />

          {/* AI Endpoints */}
          <Route
            path="/project-planning/ai/schedule-optimization"
            element={
              <ProtectedRoute>
                <ScheduleOptimizationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/schedule-optimization/history"
            element={
              <ProtectedRoute>
                <ScheduleOptimizationList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/schedule-optimization/:id"
            element={
              <ProtectedRoute>
                <AIResultDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-planning/ai/delay-analysis"
            element={
              <ProtectedRoute>
                <DelayAnalysisPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/delay-analysis/history"
            element={
              <ProtectedRoute>
                <DelayAnalysisList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/delay-analysis/:id"
            element={
              <ProtectedRoute>
                <AIResultDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-planning/ai/video-analysis"
            element={
              <ProtectedRoute>
                <VideoAnalyzerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/video-analysis/history"
            element={
              <ProtectedRoute>
                <VideoAnalyzerList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/video-analysis/:id"
            element={
              <ProtectedRoute>
                <AIResultDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-planning/ai/overall-report"
            element={
              <ProtectedRoute>
                <OverallReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/overall-report/history"
            element={
              <ProtectedRoute>
                <OverallReportList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/overall-report/:id"
            element={
              <ProtectedRoute>
                <AIResultDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/project-planning/ai/risk-forecast"
            element={
              <ProtectedRoute>
                <RiskForecastPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/risk-forecast/history"
            element={
              <ProtectedRoute>
                <RiskForecastList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-planning/ai/risk-forecast/:id"
            element={
              <ProtectedRoute>
                <AIResultDetail />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFound />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
